import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, CardActionArea,
  TextField, InputAdornment, CircularProgress, Chip, IconButton,
  Skeleton, Fade, Paper, Stack, useTheme, useMediaQuery,
  Tabs, Tab, Badge, Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  School as LevelIcon,
  TrendingUp as ProgressIcon,
  AutoStories as StoryIcon,
  AccessTime as RecentIcon,
  Explore as ExploreIcon
} from '@mui/icons-material';
import apiClient from '../services/apiClient';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from "../context/LanguageContext";

const BreadcrumbsPathList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedLanguage } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State for paths and UI
  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [recentPaths, setRecentPaths] = useState([]);
  const [currentTab, setCurrentTab] = useState(0);

  // Fetch paths on mount and when language changes
  useEffect(() => {
    fetchPaths();
  }, [selectedLanguage]);

  // Fetch paths from API
  const fetchPaths = async () => {
    setLoading(true);
    try {
      const response = await apiClient.breadcrumbs.getPaths(selectedLanguage);
      const recentResponse = await apiClient.breadcrumbs.getRecentPaths(selectedLanguage);

      setPaths(response.data.paths || []);
      setRecentPaths(recentResponse.data.paths || []);
    } catch (error) {
      console.error('Error fetching paths:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter paths based on search
  const filteredPaths = paths.filter(path => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return path.title.toLowerCase().includes(searchLower) ||
           path.description?.toLowerCase().includes(searchLower) ||
           path.tags?.some(tag => tag.toLowerCase().includes(searchLower));
  });

  // Group paths by level for better organization
  const pathsByLevel = {
    'A1': filteredPaths.filter(p => p.level === 'A1'),
    'A2': filteredPaths.filter(p => p.level === 'A2'),
    'B1': filteredPaths.filter(p => p.level === 'B1'),
    'B2': filteredPaths.filter(p => p.level === 'B2'),
    'C1': filteredPaths.filter(p => p.level === 'C1'),
    'C2': filteredPaths.filter(p => p.level === 'C2'),
  };

  // Get paths that user has started (in progress or completed)
  // Also check if all stories are completed even if user_status isn't set properly
  const continuePaths = paths.filter(p => {
    // Check explicit user_status
    if (p.user_status === 'in_progress' || p.user_status === 'completed') {
      return true;
    }
    // Also check if any stories have been started
    if (p.stories && p.stories.some(s => s.status === 'completed' || s.status === 'in_progress')) {
      return true;
    }
    // Check user_progress field if it exists
    if (p.user_progress && p.user_progress > 0) {
      return true;
    }
    return false;
  });

  // Separate them for better organization
  const inProgressPaths = continuePaths.filter(p => {
    // Path is in progress if explicitly set OR if some but not all stories are completed
    if (p.user_status === 'in_progress') return true;
    if (p.stories) {
      const hasIncomplete = p.stories.some(s => s.status !== 'completed');
      const hasCompleted = p.stories.some(s => s.status === 'completed');
      return hasIncomplete && hasCompleted;
    }
    return false;
  });

  const completedPaths = continuePaths.filter(p => {
    // Path is completed if explicitly set OR if all stories are completed
    if (p.user_status === 'completed') return true;
    if (p.stories && p.stories.length > 0) {
      return p.stories.every(s => s.status === 'completed');
    }
    return false;
  });

  // Calculate completion percentage
  const getCompletionPercentage = (path) => {
    if (!path.stories || path.stories.length === 0) return 0;
    const completedCount = path.stories.filter(story => story.status === 'completed').length;
    return Math.round((completedCount / path.stories.length) * 100);
  };

  // Navigate to path details
  const handleSelectPath = (pathId) => {
    navigate(`/breadcrumbs/paths/${pathId}`);
  };

  // Path Card Component
  const PathCard = ({ path, compact = false }) => {
    const completion = getCompletionPercentage(path);

    // Better determine if path is completed
    const isCompleted = path.user_status === 'completed' ||
                       (path.stories && path.stories.length > 0 &&
                        path.stories.every(s => s.status === 'completed'));

    // Better determine if path is in progress
    const isInProgress = !isCompleted &&
                        (path.user_status === 'in_progress' ||
                         (path.stories && path.stories.some(s => s.status === 'completed' || s.status === 'in_progress')));

    return (
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          borderRadius: compact ? 2 : 3,
          border: '1px solid',
          borderColor: 'divider',
          transition: 'all 0.2s ease',
          overflow: 'visible',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 4,
            borderColor: 'primary.main',
          }
        }}
      >
        <CardActionArea
          onClick={() => handleSelectPath(path.path_id)}
          sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
        >
          {/* Accent stripe: gold fill = in progress, solid green = completed */}
          {(isInProgress || isCompleted) && (
            <Box
              sx={{
                height: 5,
                background: isCompleted
                  ? theme.palette.success.main
                  : `linear-gradient(to right, ${theme.palette.secondary.main} ${completion}%, ${theme.palette.grey[200]} ${completion}%)`,
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
              }}
            />
          )}

          <CardContent sx={{ p: compact ? 2 : 2.5, flexGrow: 1 }}>
            {/* Title and Level Badge */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography
                  variant={compact ? "subtitle1" : "h6"}
                  sx={{
                    fontWeight: 500,
                    lineHeight: 1.2,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}
                >
                  {path.title}
                </Typography>
              </Box>
              <Chip
                label={path.level}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  bgcolor: 'primary.light',
                  color: 'white',
                  flexShrink: 0
                }}
              />
            </Box>

            {/* Description */}
            {path.description && !compact && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 2,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  lineHeight: 1.4
                }}
              >
                {path.description}
              </Typography>
            )}

            {/* Bottom Info */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mt: 'auto'
            }}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <StoryIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {path.stories_count || 0} stories
                </Typography>
              </Stack>

              {isInProgress && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: completion === 100 ? 'success.main' : 'secondary.dark'
                    }}
                  >
                    {completion}% complete
                  </Typography>
                </Stack>
              )}

              {isCompleted && (
                <Chip
                  label="Completed"
                  size="small"
                  color="success"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
    );
  };

  // Loading skeleton
  const LoadingSkeleton = () => (
    <Box sx={{ px: 2 }}>
      {[1, 2, 3].map(i => (
        <Skeleton key={i} variant="rounded" height={120} sx={{ mb: 2 }} />
      ))}
    </Box>
  );

  // Responsive grid wrapper — fills horizontal space on larger screens
  // instead of stacking every card in a single column.
  const CardGrid = ({ children }) => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(260px, 1fr))' },
        gap: 2,
      }}
    >
      {children}
    </Box>
  );

  // Tab panels content
  const tabContent = [
    {
      label: 'Continue',
      icon: <ProgressIcon />,
      badge: continuePaths.length,
      content: (
        <Stack spacing={2} sx={{ px: 2, pb: 2 }}>
          {continuePaths.length > 0 ? (
            <>
              {/* In Progress Paths */}
              {inProgressPaths.length > 0 && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ px: 1 }}>
                    In Progress
                  </Typography>
                  <CardGrid>
                    {inProgressPaths.map(path => (
                      <PathCard key={path.path_id} path={path} />
                    ))}
                  </CardGrid>
                </>
              )}

              {/* Completed Paths */}
              {completedPaths.length > 0 && (
                <>
                  {inProgressPaths.length > 0 && <Divider sx={{ my: 1 }} />}
                  <Typography variant="subtitle2" color="text.secondary" sx={{ px: 1 }}>
                    Completed
                  </Typography>
                  <CardGrid>
                    {completedPaths.map(path => (
                      <PathCard key={path.path_id} path={path} />
                    ))}
                  </CardGrid>
                </>
              )}
            </>
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default' }}>
              <Typography variant="body2" color="text.secondary">
                No paths started yet. Explore below to begin learning!
              </Typography>
            </Paper>
          )}
        </Stack>
      )
    },
    {
      label: 'Recent',
      icon: <RecentIcon />,
      badge: recentPaths.length,
      content: (
        <Box sx={{ px: 2, pb: 2 }}>
          {recentPaths.length > 0 ? (
            <CardGrid>
              {recentPaths.map(path => (
                <PathCard key={path.path_id} path={path} />
              ))}
            </CardGrid>
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default' }}>
              <Typography variant="body2" color="text.secondary">
                No recent paths yet
              </Typography>
            </Paper>
          )}
        </Box>
      )
    },
    {
      label: 'All',
      icon: <ExploreIcon />,
      content: (
        <Box sx={{ px: 2, pb: 2 }}>
          {Object.entries(pathsByLevel).map(([level, levelPaths]) => {
            if (levelPaths.length === 0) return null;

            return (
              <Box key={level} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Chip
                    label={`Level ${level}`}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      bgcolor: 'primary.main',
                      color: 'white'
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {levelPaths.length} paths
                  </Typography>
                </Box>

                <CardGrid>
                  {levelPaths.map(path => (
                    <PathCard key={path.path_id} path={path} />
                  ))}
                </CardGrid>
              </Box>
            );
          })}

          {filteredPaths.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default' }}>
              <Typography variant="body2" color="text.secondary">
                No paths found matching your search
              </Typography>
            </Paper>
          )}
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ pb: 8, minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'background.paper'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontFamily: '"Fraunces", Georgia, serif' }}>
          Voyages
        </Typography>

        {/* Search Bar */}
        <TextField
          fullWidth
          placeholder="Search paths, topics, or tags..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              borderRadius: 2,
              bgcolor: 'background.default'
            }
          }}
        />
      </Paper>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          variant={isMobile ? "fullWidth" : "standard"}
          sx={{
            minHeight: 48,
            '& .MuiTab-root': {
              minHeight: 48,
              textTransform: 'none',
              fontWeight: 500
            }
          }}
        >
          {tabContent.map((tab, index) => (
            <Tab
              key={index}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {tab.icon}
                  {!isMobile && tab.label}
                  {tab.badge > 0 && (
                    <Badge
                      badgeContent={tab.badge}
                      color="primary"
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: '0.7rem',
                          height: 18,
                          minWidth: 18
                        }
                      }}
                    />
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>
      </Box>

      {/* Content */}
      <Box sx={{ mt: 2 }}>
        {loading ? (
          <LoadingSkeleton />
        ) : (
          <Fade in timeout={300}>
            <Box>{tabContent[currentTab].content}</Box>
          </Fade>
        )}
      </Box>
    </Box>
  );
};

export default BreadcrumbsPathList;